import React, { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ExampleTabs from "../components/examples/ExampleTabs";
import ExampleCard from "../components/examples/ExampleCard";
import ApiPlayground from "../components/examples/ApiPlayground";
import UseCaseFlowSection from "../components/examples/UseCaseFlowSection";
import { Search, Close } from '@mui/icons-material';


const Examples = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const tabParam = searchParams.get('tab');
    const exampleParam = searchParams.get('example');
    const [activeTab, setActiveTab] = useState(tabParam || 'authentication');
    const [searchTerm, setSearchTerm] = useState('');

    // Mettre à jour l'URL quand le tab change
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        const newParams = new URLSearchParams(searchParams);
        newParams.set('tab', tabId);
        if (newParams.get('example')) newParams.delete('example'); // Reset example si on change de tab
        setSearchParams(newParams, { replace: true });
    };

    const examplesData = useMemo(() => ({
        authentication: {
            title: "Authentification OAuth2",
            description: "Exemples d'obtention du token d'accès avec différentes technologies",
            examples: [
                {
                    id: 'nodejs-auth',
                    title: 'Node.js - Authentification',
                    description: 'Obtenir un token d\'accès avec Node.js et axios'
                },
                {
                    id: 'ts-auth',
                    title: 'TypeScript - Authentification',
                    description: 'Obtenir un token d\'accès avec TypeScript (fetch) + typage'
                },
                {
                    id: 'python-auth',
                    title: 'Python - Authentification',
                    description: 'Obtenir un token d\'accès avec Python et requests'
                },
                {
                    id: 'php-auth',
                    title: 'PHP - Authentification',
                    description: 'Obtenir un token d\'accès avec PHP et cURL'
                },
                {
                    id: 'go-auth',
                    title: 'Go - Authentification',
                    description: 'Obtenir un token d\'accès avec Go (net/http)'
                }
            ]
        },
        payment: {
            title: "Demande de Paiement",
            description: "Exemples de création de demandes de paiement",
            examples: [
                {
                    id: 'nodejs-payment',
                    title: 'Node.js - Création Paiement',
                    description: 'Créer une demande de paiement avec Node.js'
                },
                {
                    id: 'ts-payment',
                    title: 'TypeScript - Création Paiement',
                    description: 'Créer une demande de paiement avec TypeScript (fetch)'
                },
                {
                    id: 'python-payment',
                    title: 'Python - Création Paiement',
                    description: 'Créer une demande de paiement avec Python'
                },
                {
                    id: 'php-payment',
                    title: 'PHP - Création Paiement',
                    description: 'Créer une demande de paiement avec PHP'
                },
                {
                    id: 'go-payment',
                    title: 'Go - Création Paiement',
                    description: 'Créer une demande de paiement avec Go (net/http)'
                }
            ]
        },
        verification: {
            title: "Détail d'une demande de paiement",
            description: "Récupérer le détail et le statut d'une demande par son identifiant (GET /transactions/requests/{id})",
            examples: [
                {
                    id: 'nodejs-verification',
                    title: 'Node.js - Vérification',
                    description: 'Récupérer une demande par son id avec Node.js'
                },
                {
                    id: 'ts-verification',
                    title: 'TypeScript - Vérification',
                    description: 'Vérifier le statut d\'un paiement avec TypeScript (fetch)'
                },
                {
                    id: 'python-verification',
                    title: 'Python - Vérification',
                    description: 'Récupérer une demande par son id avec Python'
                },
                {
                    id: 'php-verification',
                    title: 'PHP - Vérification',
                    description: 'Vérifier le statut d\'un paiement avec PHP (cURL)'
                },
                {
                    id: 'go-verification',
                    title: 'Go - Vérification',
                    description: 'Récupérer une demande par son id avec Go (net/http)'
                }
            ]
        },
        webhooks: {
            title: "Webhooks & Callbacks",
            description: "Exemples de gestion des webhooks et callbacks",
            examples: [
                {
                    id: 'nodejs-webhook',
                    title: 'Node.js - Webhooks',
                    description: 'Gérer les webhooks de paiement avec Node.js'
                },
                {
                    id: 'ts-webhook',
                    title: 'TypeScript - Webhooks',
                    description: 'Vérifier la signature et traiter un webhook (Express)'
                },
                {
                    id: 'python-webhook',
                    title: 'Python - Webhooks',
                    description: 'Gérer les webhooks de paiement avec Python'
                },
                {
                    id: 'php-webhook',
                    title: 'PHP - Webhooks',
                    description: 'Gérer les webhooks de paiement avec PHP'
                },
                {
                    id: 'go-webhook',
                    title: 'Go - Webhooks',
                    description: 'Gérer les webhooks de paiement avec Go (net/http)'
                }
            ]
        }
    }), []);

    // Scroll vers l'exemple si spécifié dans l'URL
    useEffect(() => {
        if (exampleParam) {
            const exampleElement = document.getElementById(`example-${exampleParam}`);
            if (exampleElement) {
                setTimeout(() => {
                    exampleElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }, 100);
            }
        }
    }, [exampleParam]);

    useEffect(() => {
        if (tabParam) {
            const found = Object.keys(examplesData).find(key => key === tabParam);
            if (found) setActiveTab(tabParam);
        }
    }, [tabParam, examplesData]);

    const filteredExamples = useMemo(() => {
        const list = examplesData[activeTab]?.examples || [];
        const q = searchTerm.trim().toLowerCase();
        if (!q) return list;
        return list.filter((ex) => {
            const hay = `${ex.id} ${ex.title} ${ex.description}`.toLowerCase();
            return hay.includes(q);
        });
    }, [activeTab, examplesData, searchTerm]);

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-950 dark:text-slate-100 pt-16">
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        Exemples de Code
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Des exemples pratiques et prêts à l'emploi pour intégrer rapidement
                        les APIs Netikash dans vos applications.
                    </p>
                </div>

                <div className="grid lg:grid-cols-5 gap-8">
                    {/* Sidebar avec onglets */}
                    <div className="lg:col-span-1">
                        <ExampleTabs
                            tabs={examplesData}
                            activeTab={activeTab}
                            onTabChange={(k) => {
                                setSearchTerm('');
                                handleTabChange(k);
                            }}
                        />
                    </div>

                    {/* Contenu principal */}
                    <div className="lg:col-span-4">
                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="p-8">
                                <UseCaseFlowSection />

                                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                                    {examplesData[activeTab].title}
                                </h2>
                                <p className="text-gray-600 mb-8">
                                    {examplesData[activeTab].description}
                                </p>

                                {/* Search */}
                                <div className="mb-8 bg-gray-50 border border-gray-200 rounded-2xl p-4">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                                            <Search className="text-amber-700" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-gray-900">Rechercher un exemple</div>
                                            <div className="text-sm text-gray-600">Langage, endpoint, mot-clé…</div>
                                        </div>
                                        {searchTerm ? (
                                            <button
                                                onClick={() => setSearchTerm('')}
                                                className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                            >
                                                <Close fontSize="small" />
                                                <span className="font-semibold text-sm">Effacer</span>
                                            </button>
                                        ) : null}
                                    </div>
                                    <div className="mt-3">
                                        <input
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            placeholder="Ex: typescript, webhook, verify, php..."
                                            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                        />
                                    </div>
                                </div>

                                {/* Grille d'exemples */}
                                <div className="grid md:grid-cols-2 gap-6 mb-8">
                                    {filteredExamples.map((example) => (
                                        <ExampleCard
                                            key={example.id}
                                            example={example}
                                            category={activeTab}
                                        />
                                    ))}
                                </div>

                                {filteredExamples.length === 0 ? (
                                    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-amber-900">
                                        <div className="font-bold">Aucun exemple trouvé</div>
                                        <div className="text-sm mt-1">
                                            Essaie un autre mot-clé (ex: <span className="font-mono">go</span>, <span className="font-mono">oauth2</span>, <span className="font-mono">webhook</span>).
                                        </div>
                                    </div>
                                ) : null}

                                {/* Playground d'API */}
                                <ApiPlayground category={activeTab} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Examples;